import React, { useEffect, useState } from "react";
import "../profile.css";
import MAvatar from "../../../components/ui/atoms/MAvatar";
import MBadge from "../../../components/ui/atoms/MBadge";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";
import { useDeleteMemberAvatar } from "@/hooks/useDeleteMemberAvatar";
import { useAvatar } from "@/hooks/useAvatar";
import { MDialog } from "@/components/ui/atoms/MDialog";
import { getMemberInfo, useProfileUpdate, parseDob } from "@/hooks/useMembers";
import FormHeader from "@/components/ui/organisms/MHeader";
import MTopBar from "@/components/ui/atoms/MTopBar";
import { useTranslation } from "react-i18next";
import { trackUserDataLoaded } from "@/utils/gtm";
import { useNavigationSource } from "@/hooks/useNavigationSource";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import { MInputField } from "@/components/ui/atoms/MInputField";
import MDateInputField from "@/components/ui/molecules/MDateInputField";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import {
  genderOptions,
  DerivedMemberType,
  MemberType,
  PetType,
  UpdateMemberContactRequest,
  isChild,
} from "@/types/member";
import { MButton } from "@/components/ui/atoms/MButtons";
import {
  IconCaretDownFilled,
  IconInfoCircle,
  IconPhone,
  IconMail,
  IconSquareRoundedCheckFilled,
} from "@tabler/icons-react";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import { isValidName, isValidEmail, isValidMobile } from "@/utils";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { MOtp } from "@/components/ui/molecules/MOtp";
import { MDialCode } from "@/components/ui/molecules/MDialCode";
import { otpService } from "@/services/otp.service";
import { sanitizedEmail } from "@/utils/member";
import { useManagedMembersContext } from "@/context/ManagedMembersContext";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useSubscription } from "@/hooks/useSubscription";

const BasicInformation: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { navigateBackIfExists } = useNavigationSource();
  const { showToast } = useToast();

  const { selectedMember, member } = useSelector((state: RootState) => ({
    selectedMember: state.auth?.selectedMember,
    member: state.auth?.member,
  }));
  const displayMember = selectedMember || member;
  const memberInfo = getMemberInfo();
  const { fetchManagedMembers } = useManagedMembersContext();

  const isPet =
    memberInfo.memberType === MemberType.PET ||
    memberInfo.derivedMemberType === DerivedMemberType.P;

  // Avatar
  const { avatarUrl, reloadAvatar } = useAvatar();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { deleteAvatar, deleting, deleteError } = useDeleteMemberAvatar();

  // Identity state
  const [firstName, setFirstName] = useState(memberInfo.firstName || "");
  const [lastName, setLastName] = useState(memberInfo.lastName || "");
  const [genderDrawerOpen, setGenderDrawerOpen] = useState(false);
  const [gender, setGender] = useState<number | undefined>(memberInfo.gender);
  const [savingIdentity, setSavingIdentity] = useState(false);

  // Pet-specific state
  const petAttrs =
    (memberInfo as any).otherAttrs || (memberInfo as any).other_attrs || {};
  const [petType, setPetType] = useState<PetType>(
    petAttrs.petType || PetType.DOG
  );
  const [breed, setBreed] = useState<string>(petAttrs.breed || "");
  const [petTypeDrawerOpen, setPetTypeDrawerOpen] = useState(false);

  let initialDob = "";
  if (memberInfo.dob) {
    initialDob = memberInfo.dob;
  } else if (memberInfo.dobMonth && memberInfo.dobYear) {
    initialDob = `${memberInfo.dobYear}-${String(memberInfo.dobMonth).padStart(
      2,
      "0"
    )}-01`;
  }
  const [dob, setDob] = useState<string>(initialDob);

  const initialFirstName = React.useRef(firstName);
  const initialLastName = React.useRef(lastName);
  const initialDobRef = React.useRef(dob);
  const initialGender = React.useRef(gender);
  const initialPetType = React.useRef(petType);
  const initialBreed = React.useRef(breed);

  function normalizeDob(val?: string) {
    if (!val) return "";
    const [y, m] = val.split("-");
    return `${y}-${m}`;
  }

  const firstNameValid = isValidName(firstName.trim());
  const lastNameValid = isValidName(lastName?.trim());

  const isIdentityChanged = isPet
    ? firstName !== initialFirstName.current ||
      breed !== initialBreed.current ||
      petType !== initialPetType.current ||
      normalizeDob(dob) !== normalizeDob(initialDobRef.current) ||
      gender !== initialGender.current
    : firstName !== initialFirstName.current ||
      lastName !== initialLastName.current ||
      normalizeDob(dob) !== normalizeDob(initialDobRef.current) ||
      gender !== initialGender.current;

  const {
    subscription,
    loading: subscriptionLoading,
    fetchCurrentSubscription,
  } = useSubscription();
  const isCorporate = subscription?.subscriptionSource === "corporate";

  useEffect(() => {
    fetchCurrentSubscription();
  }, [fetchCurrentSubscription]);

  // Contact state
  const [email, setEmail] = useState(memberInfo.email || "");
  const [mobile, setMobile] = useState(memberInfo.mobile || "");
  const [dialCode, setDialCode] = useState(memberInfo.dialCode || "+91");
  const [showDialCodePicker, setShowDialCodePicker] = useState(false);
  const [savingContact, setSavingContact] = useState(false);

  const isEmailChanged = !isCorporate && email !== (memberInfo.email || "");
  const isMobileChanged = mobile !== (memberInfo.mobile || "");
  const isContactChanged =
    isEmailChanged ||
    isMobileChanged ||
    dialCode !== (memberInfo.dialCode || "+91");

  // OTP state
  const [showOtpDrawer, setShowOtpDrawer] = useState(false);
  const [showEmailInfoDrawer, setShowEmailInfoDrawer] = useState(false);
  const [otpField, setOtpField] = useState<null | "email" | "mobile">(null);
  const [otpTargetValue, setOtpTargetValue] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isMobileVerified, setIsMobileVerified] = useState(false);
  const [showMobileVerifiedMsg, setShowMobileVerifiedMsg] = useState(false);
  const [mobileVerifiedFade, setMobileVerifiedFade] = useState(false);

  useEffect(() => {
    if (isMobileVerified) {
      setShowMobileVerifiedMsg(true);
      setMobileVerifiedFade(false);
      const fadeTimer = setTimeout(() => setMobileVerifiedFade(true), 2000);
      const hideTimer = setTimeout(() => setShowMobileVerifiedMsg(false), 2500);
      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    } else {
      setShowMobileVerifiedMsg(false);
      setMobileVerifiedFade(false);
    }
  }, [isMobileVerified]);

  const { handleProfileUpdate, handleContactUpdate } = useProfileUpdate();

  const [show, setShow] = useState(false);
  useEffect(() => {
    setShow(true);
  }, []);

  const [isInputFocused, setIsInputFocused] = useState(false);
  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA") {
        setIsInputFocused(true);
      }
    };
    const handleFocusOut = () => {
      requestAnimationFrame(() => {
        const active = document.activeElement;
        if (
          !active ||
          (active.tagName !== "INPUT" && active.tagName !== "TEXTAREA")
        ) {
          setIsInputFocused(false);
        }
      });
    };
    window.addEventListener("focusin", handleFocusIn);
    window.addEventListener("focusout", handleFocusOut);
    return () => {
      window.removeEventListener("focusin", handleFocusIn);
      window.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  const mobileRowRef = React.useRef<HTMLDivElement>(null);
  const emailRowRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const scrollFieldIntoView = (ref: React.RefObject<HTMLDivElement>) => {
    setTimeout(() => {
      if (!ref.current || !scrollContainerRef.current) return;
      const container = scrollContainerRef.current;
      const el = ref.current;
      // Use visualViewport height if available (shrinks when iOS keyboard opens)
      const visibleHeight = window.visualViewport?.height ?? window.innerHeight;
      const elRect = el.getBoundingClientRect();
      // Scroll up enough so the bottom of the field is 60px above the keyboard
      const gap = 60;
      const overshoot = elRect.bottom - (visibleHeight - gap);
      if (overshoot > 0) {
        container.scrollBy({ top: overshoot, behavior: "smooth" });
      }
    }, 400);
  };

  useEffect(() => {
    if (!memberInfo?.id) return;
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_IDENTITY,
    });
  }, [memberInfo?.id]);

  const handleBack = () => {
    const didNavigate = navigateBackIfExists();
    if (!didNavigate) {
      navigate("/profile");
    }
  };

  const handleDeleteAvatar = async () => {
    const success = await deleteAvatar(displayMember.id, avatarUrl);
    if (success) {
      await reloadAvatar();
      setShowDeleteDialog(false);
    }
  };

  const isDoneDisabled = (): boolean => {
    const anythingChanged = isIdentityChanged || isContactChanged;
    if (!anythingChanged) return true;
    if (savingIdentity || savingContact) return true;
    if (
      isIdentityChanged &&
      isPet &&
      (!firstName || !firstNameValid || !dob || !gender)
    )
      return true;
    if (
      isIdentityChanged &&
      !isPet &&
      (!firstName ||
        !lastName ||
        !dob ||
        !gender ||
        !firstNameValid ||
        !lastNameValid)
    )
      return true;
    if (isEmailChanged && (!isValidEmail(email) || !isEmailVerified))
      return true;
    if (isMobileChanged && (!isValidMobile(mobile) || !isMobileVerified))
      return true;
    return false;
  };

  async function handleDone() {
    // Save identity if changed
    if (isIdentityChanged) {
      const { dob_day, dob_month, dob_year } = parseDob(dob);
      const updateData = isPet
        ? {
            first_name: firstName.trim(),
            gender,
            dob_day,
            dob_month,
            dob_year,
            other_attrs: { petType, breed },
          }
        : {
            first_name: firstName.trim(),
            last_name: lastName?.trim(),
            gender,
            dob_day,
            dob_month,
            dob_year,
          };
      const result = await handleProfileUpdate({
        memberId: memberInfo.id,
        updateData,
        isChanged: isIdentityChanged,
        onSavingChange: setSavingIdentity,
      });
      if (!result.success) {
        showToast({ message: t("errors.toast"), variant: "error" });
        return;
      }
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
        screenName: ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_IDENTITY_COMPLETE,
      });
      // Reset initial refs so changed state clears
      initialFirstName.current = firstName;
      initialLastName.current = lastName;
      initialDobRef.current = dob;
      initialGender.current = gender;
      initialPetType.current = petType;
      initialBreed.current = breed;
      await fetchManagedMembers(true);
    }

    // Save contact if changed
    if (isContactChanged) {
      const updateData: UpdateMemberContactRequest = {};
      if (email && !isCorporate) updateData.email = sanitizedEmail(email);
      if (mobile) {
        updateData.mobile = mobile;
        updateData.dialCode = dialCode;
      }
      const result = await handleContactUpdate({
        memberId: memberInfo.id,
        updateData,
        isChanged: isContactChanged,
        onSavingChange: setSavingContact,
      });
      if (result?.success === false) {
        showToast({ message: t("errors.toast"), variant: "error" });
        return;
      }
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
        screenName: ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_CONTACT_COMPLETE,
      });
    }

    showToast({
      message: t("profile.identityUpdateSuccess"),
      variant: "success",
    });
    const didNavigate = navigateBackIfExists();
    if (!didNavigate) {
      navigate("/profile");
    }
  }

  function extractOtpError(err: any): string {
    const data = err?.response?.data;
    if (data?.message?.message) return data.message.message;
    if (typeof data?.message === "string") return data.message;
    return t("errors.toast");
  }

  const handleSendMobileOtp = async () => {
    setOtpLoading(true);
    setOtpField("mobile");
    setOtpTargetValue(mobile);
    setShowOtpDrawer(true);
    try {
      await otpService.sendOtp({
        send_to: mobile,
        dialCode,
        mode: "sms",
        type: "member_update",
      });
    } catch (err: any) {
      showToast({ message: extractOtpError(err), variant: "error" });
      setShowOtpDrawer(false);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSendEmailOtp = async () => {
    setOtpLoading(true);
    setOtpField("email");
    setOtpTargetValue(email);
    setShowOtpDrawer(true);
    try {
      await otpService.sendOtp({
        send_to: email,
        mode: "email",
        type: "member_update",
      });
    } catch (err: any) {
      showToast({ message: extractOtpError(err), variant: "error" });
      setShowOtpDrawer(false);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpVerify = async (otpValue: string): Promise<true | string> => {
    setOtpLoading(true);
    try {
      const response = await otpService.verifyOtp({
        send_to: otpTargetValue,
        dialCode: otpField === "mobile" ? dialCode : undefined,
        mode: otpField === "mobile" ? "sms" : "email",
        type: "member_update",
        otp: otpValue,
      });
      const expectedTarget =
        otpField === "email"
          ? otpTargetValue.toLowerCase().trim()
          : otpTargetValue;
      const receivedTarget = response.data?.target || "";
      if (!(response.data?.verified && receivedTarget === expectedTarget)) {
        return "Verification failed: target mismatch";
      }
      setShowOtpDrawer(false);
      if (otpField === "email") setIsEmailVerified(true);
      if (otpField === "mobile") setIsMobileVerified(true);
      setTimeout(() => setOtpField(null), 250);
      return true;
    } catch (err: any) {
      if (err?.response?.status === 400)
        return "The otp you entered is incorrect. Please try again.";
      return err?.message || "Verification failed";
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpResend = async () => {
    setOtpLoading(true);
    try {
      await otpService.sendOtp({
        send_to: otpTargetValue,
        dialCode: otpField === "mobile" ? dialCode : undefined,
        mode: otpField === "mobile" ? "sms" : "email",
        type: "member_update",
      });
    } catch {
      showToast({ message: t("errors.toast"), variant: "error" });
    } finally {
      setOtpLoading(false);
    }
  };

  const genderLabel = genderOptions.find((opt) => opt.value === gender)?.label;

  return (
    <div
      className={`flex-1 flex flex-col min-h-0 transition-all duration-500 ease-out ${
        show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      {/* Scrollable content area */}
      <div
        ref={scrollContainerRef}
        className="flex-1 min-h-0 overflow-y-auto px-6"
      >
        {/* Sticky header */}
        <div className="profile-sticky-header">
          <MTopBar
            hideProfilePicker
            hideBack
            style={{ background: "#FBF6FF" }}
          />
          <FormHeader
            subHeading="Basic information"
            module="basic-info"
            memberInfo={memberInfo}
            showGradient
            title={<></>}
            description={<></>}
            hideBack={false}
            onBack={handleBack}
          />
        </div>

        {/* Avatar */}
        <div className="flex flex-col items-center gap-3 mt-4">
          {displayMember.avatar && avatarUrl ? (
            <MAvatar
              size="xl"
              text={`${displayMember?.first_name || ""}${
                displayMember?.last_name ? " " + displayMember.last_name : ""
              }`.trim()}
              type="dp"
              src={avatarUrl}
              className="profile-avatar-lg"
              showBadge
              memberId={
                displayMember?.id ? String(displayMember.id) : undefined
              }
            />
          ) : (
            <MAvatar
              size="xl"
              text={`${displayMember?.first_name || ""}${
                displayMember?.last_name ? " " + displayMember.last_name : ""
              }`.trim()}
              type="no-dp"
              src=""
              className="profile-avatar-lg"
              showBadge
              memberId={
                displayMember?.id ? String(displayMember.id) : undefined
              }
            />
          )}
          {(displayMember?.registered_member ||
            displayMember?.family_member_type === "fo") && (
            <MBadge variant="purple-inverse">Maatra account</MBadge>
          )}
        </div>

        <MDialog
          isOpen={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          title="Delete Avatar"
          primaryCtaText={deleting ? "Deleting..." : "Delete"}
          onPrimaryCta={handleDeleteAvatar}
          isPrimaryCtaLoading={deleting}
          isPrimaryCtaDisabled={deleting}
          secondaryCtaText="Cancel"
          onSecondaryCta={() => setShowDeleteDialog(false)}
          destructivePrimary
        >
          <div>Are you sure you want to delete your avatar?</div>
          {deleteError && (
            <div className="text-red-600 mt-2">{deleteError}</div>
          )}
        </MDialog>

        {/* Identity + Contact Fields */}
        <MSpacer mt={24} />
        <div className="basic-info-fields">
          <form className="flex flex-col gap-[24px]">
            <MInputField
              placeholder={t("common.notAnswered")}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              variant="Underlined"
              size="md"
              showLabel
              label={isPet ? t("identity.fullName") : t("basicInfo.firstName")}
              maxLength={20}
            />
            {isPet ? (
              <>
                <MInputField
                  placeholder={t("common.notAnswered")}
                  value={petType ? t(`identity.typeOfPetMap.${petType}`) : ""}
                  label={t("identity.typeOfPet")}
                  variant="Underlined"
                  size="md"
                  showLabel
                  readOnly
                  icon={<IconCaretDownFilled />}
                  showIcon
                  onClick={() => setPetTypeDrawerOpen(true)}
                  aria-label={t("identity.typeOfPet")}
                />
                <MDropDownDrawer
                  open={petTypeDrawerOpen}
                  onClose={() => setPetTypeDrawerOpen(false)}
                  title={t("identity.typeOfPet")}
                  items={[
                    {
                      value: PetType.DOG,
                      label: t(`identity.typeOfPetMap.${PetType.DOG}`),
                    },
                    {
                      value: PetType.CAT,
                      label: t(`identity.typeOfPetMap.${PetType.CAT}`),
                    },
                  ]}
                  initialValue={petType}
                  onSelect={(val) => {
                    setPetType(val as PetType);
                    setPetTypeDrawerOpen(false);
                  }}
                  variant="single"
                />
                <MInputField
                  placeholder={t("common.notAnswered")}
                  value={breed}
                  onChange={(e) => setBreed(e.target.value)}
                  variant="Underlined"
                  size="md"
                  showLabel
                  label={t("identity.enterBreed")}
                />
                <MDateInputField
                  placeholder={t("common.notAnswered")}
                  value={
                    dob && /^\d{4}-\d{2}$/.test(dob)
                      ? new Date(
                          Number(dob.split("-")[0]),
                          Number(dob.split("-")[1]) - 1
                        )
                      : dob && /^\d{4}-\d{2}-\d{2}$/.test(dob)
                      ? new Date(dob)
                      : undefined
                  }
                  onDateChange={(date) =>
                    setDob(
                      date
                        ? `${date.getFullYear()}-${String(
                            date.getMonth() + 1
                          ).padStart(2, "0")}`
                        : ""
                    )
                  }
                  variant="Underlined"
                  size="md"
                  showLabel
                  initialPickerValue={
                    (dob && new Date(dob)) || new Date(2000, 0, 1)
                  }
                  label={t("identity.dateOfBirth")}
                  datePickerTitle="Select Month & Year"
                  hideDate={true}
                  formatDate={(date?: Date) =>
                    date
                      ? date.toLocaleString("en-US", {
                          month: "short",
                          year: "numeric",
                        })
                      : ""
                  }
                />
                <MInputField
                  placeholder={t("common.notAnswered")}
                  value={genderLabel}
                  label={t("identity.ob.whatIsYourGender")}
                  variant="Underlined"
                  size="md"
                  readOnly
                  icon={<IconCaretDownFilled />}
                  showIcon
                  onClick={() => setGenderDrawerOpen(true)}
                  aria-label={t("identity.ob.whatIsYourGender")}
                />
                <MDropDownDrawer
                  open={genderDrawerOpen}
                  onClose={() => setGenderDrawerOpen(false)}
                  title={t("common.selectGender")}
                  items={genderOptions.map((opt) => ({
                    value: String(opt.value),
                    label: opt.label,
                  }))}
                  initialValue={gender !== undefined ? String(gender) : ""}
                  onSelect={(val) => {
                    setGender(val ? Number(val) : undefined);
                    setGenderDrawerOpen(false);
                  }}
                  variant="single"
                />
              </>
            ) : (
              <>
                <MInputField
                  placeholder={t("common.notAnswered")}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  variant="Underlined"
                  size="md"
                  showLabel
                  label={t("basicInfo.lastName")}
                  maxLength={20}
                />
                <MDateInputField
                  placeholder={t("common.notAnswered")}
                  value={
                    dob && /^\d{4}-\d{2}$/.test(dob)
                      ? new Date(
                          Number(dob.split("-")[0]),
                          Number(dob.split("-")[1]) - 1
                        )
                      : dob && /^\d{4}-\d{2}-\d{2}$/.test(dob)
                      ? new Date(dob)
                      : undefined
                  }
                  onDateChange={(date) =>
                    setDob(
                      date
                        ? `${date.getFullYear()}-${String(
                            date.getMonth() + 1
                          ).padStart(2, "0")}`
                        : ""
                    )
                  }
                  variant="Underlined"
                  size="md"
                  showLabel
                  initialPickerValue={
                    (dob && new Date(dob)) || new Date(2000, 0, 1)
                  }
                  label={t("basicInfo.dateOfBirth")}
                  datePickerTitle="Select Month & Year"
                  hideDate={true}
                  formatDate={(date?: Date) =>
                    date
                      ? date.toLocaleString("en-US", {
                          month: "short",
                          year: "numeric",
                        })
                      : ""
                  }
                  maxDate={(() => {
                    // Restrict to 18+ for FOs, or for any member whose
                    // existing DOB already makes them an adult — adults must
                    // not be downgraded to a child profile via DOB edit.
                    const existingIsAdult =
                      !!(memberInfo.dobYear && memberInfo.dobMonth) &&
                      !isChild(memberInfo);
                    if (
                      memberInfo.family_member_type === "fo" ||
                      existingIsAdult
                    ) {
                      const today = new Date();
                      return new Date(
                        today.getFullYear() - 18,
                        today.getMonth() + 1,
                        0
                      );
                    }
                    return undefined;
                  })()}
                />
                <MInputField
                  placeholder={t("common.notAnswered")}
                  value={genderLabel}
                  label={t("basicInfo.gender")}
                  variant="Underlined"
                  size="md"
                  readOnly
                  icon={<IconCaretDownFilled />}
                  showIcon
                  onClick={() => setGenderDrawerOpen(true)}
                  aria-label={t("basicInfo.gender")}
                />
                <MDropDownDrawer
                  open={genderDrawerOpen}
                  onClose={() => setGenderDrawerOpen(false)}
                  title={t("common.selectGender")}
                  items={genderOptions.map((opt) => ({
                    value: String(opt.value),
                    label: opt.label,
                  }))}
                  initialValue={gender !== undefined ? String(gender) : ""}
                  onSelect={(val) => {
                    setGender(val ? Number(val) : undefined);
                    setGenderDrawerOpen(false);
                  }}
                  variant="single"
                />
              </>
            )}
          </form>

          {/* Contact Fields — only for members with their own Maatra account */}
          {(displayMember?.registered_member ||
            displayMember?.family_member_type === "fo") && (
            <div className="flex flex-col pb-8">
              {/* Mobile row */}
              <div
                ref={mobileRowRef}
                className="flex items-start gap-3 border-[var(--colour-border-divider-n40,#E2E2E2)]"
                style={{ paddingTop: "40px" }}
                onFocusCapture={() => scrollFieldIntoView(mobileRowRef)}
              >
                <IconPhone
                  size={22}
                  className="text-[#8764BB] flex-shrink-0 mt-5"
                />
                <div className="flex-1">
                  <div className="flex gap-2 items-end">
                    <div
                      className="flex-shrink-0 cursor-pointer"
                      style={{ width: 54 }}
                      onClick={() => setShowDialCodePicker(true)}
                    >
                      <MInputField
                        showLabel
                        label={t("basicInfo.mobileNumber")}
                        value={dialCode}
                        className="cursor-pointer text-base"
                        variant="Underlined"
                        size="md"
                        readOnly
                      />
                    </div>
                    <MDialCode
                      open={showDialCodePicker}
                      onClose={() => setShowDialCodePicker(false)}
                      onDone={(code) => {
                        setDialCode(code);
                        setIsMobileVerified(false);
                        setShowDialCodePicker(false);
                      }}
                      initialValue={dialCode}
                    />
                    <div className="flex-1">
                      <MInputField
                        placeholder={t("common.notAnswered")}
                        value={mobile}
                        onChange={(e) => {
                          setMobile(e.target.value);
                          setIsMobileVerified(false);
                        }}
                        variant="Underlined"
                        size="md"
                        type="tel"
                        inputMode="numeric"
                        state={
                          isMobileVerified
                            ? "Success"
                            : isMobileChanged &&
                              mobile &&
                              !isValidMobile(mobile)
                            ? "Error"
                            : "Default"
                        }
                      />
                    </div>
                  </div>
                  {showMobileVerifiedMsg && (
                    <div
                      className="flex items-center gap-2 mt-3 font-medium text-[#217B57]"
                      style={{
                        transition: "opacity 0.4s",
                        opacity: mobileVerifiedFade ? 0 : 1,
                      }}
                    >
                      <IconSquareRoundedCheckFilled size={16} color="#217B57" />
                      <span>{t("common.verified")}</span>
                    </div>
                  )}
                  {isMobileChanged &&
                    !isMobileVerified &&
                    isValidMobile(mobile) && (
                      <MButton
                        size="sm"
                        variant="link"
                        text={t("common.verify")}
                        onClick={handleSendMobileOtp}
                        className="mt-1 px-0"
                      />
                    )}
                </div>
              </div>

              {/* Email row */}
              <div
                ref={emailRowRef}
                className="flex items-start gap-3 border-[var(--colour-border-divider-n40,#E2E2E2)]"
                style={{ paddingTop: "24px" }}
                onFocusCapture={() =>
                  !isCorporate && scrollFieldIntoView(emailRowRef)
                }
              >
                <IconMail
                  size={22}
                  className="text-[#8764BB] flex-shrink-0 mt-5"
                />
                <div className="flex-1">
                  {isCorporate || subscriptionLoading ? (
                    <>
                      <div
                        className={
                          isCorporate
                            ? "[&_input]:text-[#AEAEAE] [&_input]:[color:#AEAEAE]"
                            : ""
                        }
                      >
                        <MInputField
                          placeholder={t("common.notAnswered")}
                          value={email}
                          variant="Underlined"
                          size="md"
                          showLabel
                          label={t("basicInfo.email")}
                          readOnly
                          icon={
                            isCorporate ? (
                              <IconInfoCircle size={16} color="#8764BB" />
                            ) : undefined
                          }
                          showIcon={isCorporate}
                          onIconClick={
                            isCorporate
                              ? () => setShowEmailInfoDrawer(true)
                              : undefined
                          }
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <MInputField
                        placeholder={t("common.notAnswered")}
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setIsEmailVerified(false);
                        }}
                        variant="Underlined"
                        size="md"
                        showLabel
                        label={t("basicInfo.email")}
                        type="email"
                        inputMode="email"
                        state={
                          isEmailVerified
                            ? "Success"
                            : isEmailChanged && email && !isValidEmail(email)
                            ? "Error"
                            : "Default"
                        }
                        stateMessage={
                          isEmailVerified ? t("common.verified") : ""
                        }
                      />
                      {isEmailChanged &&
                        !isEmailVerified &&
                        isValidEmail(email) && (
                          <MButton
                            size="sm"
                            variant="link"
                            text={t("common.verify")}
                            onClick={handleSendEmailOtp}
                            className="mt-1 px-0"
                          />
                        )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        {/* end basic-info-fields */}
      </div>

      {/* Done button — hidden while keyboard is open */}
      {!isInputFocused && (
        <div className="shrink-0 flex px-6 pt-4 pb-[calc(16px+env(safe-area-inset-bottom))] bg-white border-[var(--colour-border-divider-n40,#E2E2E2)]">
          <MButton
            size="lg"
            variant="filled"
            text={t("common.done")}
            className="flex-1"
            disabled={isDoneDisabled()}
            onClick={handleDone}
          />
        </div>
      )}

      {/* OTP Drawer */}
      <MOtp
        open={showOtpDrawer}
        onClose={() => setShowOtpDrawer(false)}
        onDone={handleOtpVerify}
        onResend={handleOtpResend}
        onEdit={() => setShowOtpDrawer(false)}
        sendTo={otpTargetValue}
        loading={otpLoading}
        title={`Verify ${otpField === "email" ? "Email" : "Mobile"}`}
        resendTimer={30}
      />

      {/* Corporate email info drawer */}
      <FooterDrawer
        open={showEmailInfoDrawer}
        onClose={() => setShowEmailInfoDrawer(false)}
        title={t("basicInfo.email")}
        height="20vh"
      >
        <div className="px-6 pt-8 pb-0 text-base font-medium leading-[140%] text-[#2A2A2A]">
          {t("basicInfo.corporateEmailLocked")}
        </div>
      </FooterDrawer>
    </div>
  );
};

export default BasicInformation;
